import { Segment } from '@dna/analytics'
import { RespondToViewport, WithProfiler } from '@dna/mixins'
import { chevron, minus, plus } from '@dna/paths'
import styles from '@dna/styles'
import { t } from '@dna/utils'
import {
  LitElement,
  createRef,
  css,
  html,
  nothing,
  ref,
  repeat,
  when,
} from '@lit'

class DNAGridSort extends WithProfiler(RespondToViewport(LitElement)) {
  static properties = {
    sort: {
      type: Object,
    },
    hidePriceControlOptions: {
      type: Boolean,
    },
    showListBox: {
      type: Boolean,
      state: true,
    },
    exposeFilters: {
      type: Boolean,
      attribute: 'expose-filters',
    },
  }

  constructor() {
    super()

    this._setMobileBreakpoints(['mobile', 'tablet'])
    this.sort = {
      options: [],
      selected: '',
    }
    this.hidePriceControlOptions = false
    this.showListBox = false
    this.$combobox = createRef()
    this.$optionsList = createRef()
    this.$firstOption = createRef()
    this.$lastOption = createRef()
  }

  connectedCallback() {
    super.connectedCallback()
    this.router = __DNA_GLOBAL__.utils.queryRouter
  }

  disconnectedCallback() {
    super.disconnectedCallback()
  }

  _handleShowListBox() {
    this.showListBox = !this.showListBox
    if (this.showListBox) {
      this.updateComplete.then(() => this.$firstOption?.value?.focus())
      this._trackSortsViewed()
    } else {
      this.$combobox?.value?.focus()
    }
  }

  _sortChange(event) {
    const selectedValue = event.currentTarget.getAttribute('value')?.split('|')
    if (!selectedValue) return

    const sortValue = this.sort?.options?.find((option) => {
      return (
        option.sort_by === selectedValue[0] &&
        option.sort_order === selectedValue[1]
      )
    })

    if (sortValue) {
      this.router.query.update({
        page: 1,
        sort: sortValue.sort_by,
        sort_order: sortValue.sort_order,
      })
      this._handleShowListBox()
      this._trackSortSelected(sortValue)
    }
  }

  _handleSortKeydown(event, index) {
    event.preventDefault()
    if (event.key === 'Enter' || event.key === ' ') {
      this._sortChange(event)
    } else if (event.key === 'Escape') {
      this._handleShowListBox()
    } else if (
      event.key === 'ArrowUp' ||
      (event.key === 'Tab' && event.shiftKey)
    ) {
      if (this.shadowRoot.activeElement === this.$firstOption?.value) {
        this.$lastOption?.value?.focus()
      } else {
        this.$optionsList?.value?.children[index - 1]?.focus()
      }
    } else if (event.key === 'Tab' || event.key === 'ArrowDown') {
      if (
        this.shadowRoot.activeElement ===
        this.$optionsList?.value?.children[
          this.$optionsList.value.children.length - 1
        ]
      ) {
        this.$firstOption?.value?.focus()
      } else {
        this.$optionsList?.value?.children[index + 1]?.focus()
      }
    }
  }

  _trackSortsViewed() {
    // @segment
    Segment.protect(() => {
      Segment.track(Segment.CONSTANTS.EVENTS.SORTS_VIEWED, {
        sorts: this.sort.options
          .map((sort) => sort.display_name)
          ?.toString()
          ?.replaceAll(',', ', '),
      })
    })
  }

  _trackSortSelected(sort) {
    // @segment
    Segment.protect(() => {
      Segment.track(Segment.CONSTANTS.EVENTS.SORT_ADDED, {
        sort_name: sort.display_name,
      })
    })
  }

  _renderSortDropdown() {
    return html`
      <div class="p2 sorting-options${this.exposeFilters ? ' sorting-options--buckets' : ''}">
        <div class="sorting-options__select flex">
          <span
            class="select__prefix"
            id="sortLabel"
          >
            ${t('sections.collection_template.sort_by_label')}:
          </span>
          <ul
            ${ref(this.$combobox)}
            class="select__label"
            aria-controls="sortList"
            aria-expanded=${this.showListBox}
            aria-labelledby="sortLabel sortSelection"
            role="combobox"
            tabindex="0"
            value=${this.sort?.selected}
            @click=${this._handleShowListBox}
            @keydown=${(e) => {
              if (
                e.key === 'Enter' ||
                e.key === ' ' ||
                (e.key === 'ArrowDown' && !this.showListBox)
              ) {
                e.preventDefault()
                this._handleShowListBox()
              }
            }}
          >
            <li id="sortSelection">
              ${repeat(
                this.sort?.options,
                (option) => `${option.sort_by}|${option.sort_order}`,
                (option) =>
                  `${option.sort_by}|${option.sort_order}` ===
                  this.sort.selected
                    ? option.display_name
                    : nothing,
              )}
            </li>
          </ul>
          <button
            class="select__expand-icon${this.showListBox ? ' active' : ''}"
            tabindex="-1"
            @click=${this._handleShowListBox}
          >${chevron}</button>
        </div>
        <div
          ${ref(this.$optionsList)}
          class="sorting-options__list${this.showListBox ? ' active' : ''}"
          id="sortList"
          aria-label="Select a sorting option"
          role="listbox"
        >
          ${repeat(
            this.sort?.options,
            (option) => `${option.sort_by}|${option.sort_order}`,
            (option, index) => {
              const shouldHidePriceControlOptions =
                this.hidePriceControlOptions &&
                (option.display_name === 'Price (High to Low)' ||
                  option.display_name === 'Price (Low to High)')
              if (!shouldHidePriceControlOptions) {
                return html`
                  <div
                    ${
                      index === 0
                        ? ref(this.$firstOption)
                        : index === this.sort.options.length - 1
                          ? ref(this.$lastOption)
                          : nothing
                    }
                    class="sorting-options__option${`${option.sort_by}|${option.sort_order}` === this.sort.selected ? ' sorting-options__option--selected' : ''}"
                    aria-selected=${`${option.sort_by}|${option.sort_order}` === this.sort.selected}
                    role="option"
                    tabindex="0"
                    value=${option.sort_by}|${option.sort_order}
                    @click=${this._sortChange}
                    @keydown=${(e) => {
                      this._handleSortKeydown(e, index)
                    }}
                  >${option.display_name}</div>
                `
              }
            },
          )}
        </div>
      </div>
    `
  }

  _renderSortAccordion() {
    return html`
      <div class="container">
        <sl-details summary="${t('sections.collection_template.sort_by_label')}">
          <sl-radio-group
            class="radio-group"
            role="radiogroup"
            aria-label=${t('sections.collection_template.filter_accessible_text')}
            value="relevance|descending"
          >
            ${repeat(
              this.sort?.options,
              (option) => `${option.sort_by}|${option.sort_order}`,
              (option, index) => {
                const shouldHidePriceControlOptions =
                  this.hidePriceControlOptions &&
                  (option.display_name === 'Price (High to Low)' ||
                    option.display_name === 'Price (Low to High)')
                if (!shouldHidePriceControlOptions) {
                  return html`
                    <sl-radio
                      class="sorting-options__option${`${option.sort_by}|${option.sort_order}` === this.sort.selected ? ' sorting-options__option--selected' : ''}"
                      role="radio"
                      aria-checked=${`${option.sort_by}|${option.sort_order}` === this.sort.selected}
                      value=${option.sort_by}|${option.sort_order}
                      @click=${this._sortChange}
                    >
                      <span class="label p2">${option.display_name}</span>
                    </sl-radio>
                  `
                }
              },
            )}
          </sl-radio-group>
          <span slot="expand-icon">${plus}</span>
          <span slot="collapse-icon">${minus}</span>
        </sl-details>
      </div>
    `
  }

  render() {
    return html`
      ${when(
        this.exposeFilters && this.isMobile,
        () => this._renderSortAccordion(),
        () => this._renderSortDropdown(),
      )}
    `
  }

  static styles = [
    styles.a11y,
    styles.container,
    styles.flex,
    styles.typography,
    css`
      * {
        box-sizing: border-box;
      }

      .sorting-options {
        color: var(--dna-color-midnight);
        font: var(--dna-body-p2-font);
        font-size: 13px;
        margin: var(--dna-space-2) 0;
        position: relative;
      }

      .sorting-options__select {
        background-color: transparent;
        color: var(--dna-color-midnight);
      }

      .sorting-options__select .select__prefix {
        font: var(--dna-body-font-p3);
        font-size: 13px;
        margin-right: var(--dna-space-2);
      }

      .sorting-options__select .select__label {
        cursor: pointer;
        font: var(--dna-body-font-p3);
        font-size: 13px;
        list-style: none;
        margin: 0;
        padding: 0 calc(var(--dna-space-5) / 2) 0 0;
      }

      .select__expand-icon {
        background-color: transparent;
        border: none;
        cursor: pointer;
        display: grid;
        margin: 0;
        padding: 0;
        place-items: center;
      }

      .select__expand-icon svg {
        width: var(--dna-space-4);
        height: var(--dna-space-4);
        transform: rotate(90deg);
      }

      .select__expand-icon.active svg {
        transform: rotate(-90deg);
      }

      .sorting-options__list {
        background-color: var( --dna-brand-reverse);
        border: 1px solid var(--dna-color-light-grey);
        border-radius: 0;
        display: none;
        flex-direction: column;
        gap: var(--dna-space-4);
        min-width: 168px;
        padding: var(--dna-space-4);
        position: absolute;
        top: calc(1em + var(--dna-space-3));
        right: 0;
      }

      .sorting-options__list.active {
        display: flex;
      }

      .sorting-options__option {
        cursor: pointer;
      }

      .sorting-options__option--selected {
        font-weight: 600;
      }

      /* breakpoint-tablet */
      @media screen and (min-width: 768px) {
        .sorting-options {
          font-size: 14px;
          line-height: 20px;
        }

        .sorting-options__list {
          min-width: 220px;
          padding: var(--dna-space-6);
        }
      }

      /* breakpoint-desktop */
      @media screen and (min-width: 1024px) {
        :host {
          flex: 0 0 auto;
        }
        .sorting-options {
          margin: 0;
        }

        .sorting-options__select .select__prefix {
          font: var(--dna-body-p1-font);
        }
        .sorting-options__select .select__label {
          font: var(--dna-label-l-font);
          line-height: var(--dna-space-6);
        }
      }

      .sorting-options--buckets {
        @media screen and (min-width: 1440px) {
          font: var(--dna-body-p1-font);
        }
        .sorting-options__list {
          top: calc(1em + var(--dna-space-5) + var(--dna-space-1));
          right: -1em;

          @media screen and (min-width: 1440px) {
            font: var(--dna-body-p1-font);
          }
        }
      }

      @media screen and (max-width: 1023px) {
        .container {
          padding-left: 20px;
          padding-right: 20px;
        }
        sl-radio-group::part(form-control-input) {
          display: flex;
          flex-direction: column;
          gap: var(--dna-space-6);
          padding: 0 var(--dna-space-1);
        }
        sl-details::part(summary-icon) {
          /* Disable the expand/collapse animation */
          rotate: none;
          font-size: 24px;
        }
        sl-details::part(base) {
          border: none;
          border-radius: 0;
          border-bottom: 1px solid var(--dna-color-light-grey);
        }
        sl-details:first-child::part(base) {
          border-top: none;
        }
        sl-details::part(header) {
          padding: var(--dna-space-5) 0;
          --sl-focus-ring: 1px solid var(--dna-color-focus);
          --sl-focus-ring-offset: calc(var(--dna-space-0-5) * -1);
          font: var(--dna-label-m-font);
          font-weight: normal;
          letter-spacing: var(--dna-space-0-5);
          text-transform: uppercase;
        }
        sl-details::part(summary) {
          display: flex;
          flex: 1 1 auto;
          align-items: center;
        }
        sl-details::part(content) {
          padding: 0 0 var(--dna-space-5) 0;
        }
        sl-details sl-radio {
          position: relative;
        }
        sl-details sl-radio::part(control) {
          border-radius: 0;
        }
        sl-details sl-radio::part(control control--checked) {
          border-color: var(--dna-brand-primary);
          background-color: var(--dna-brand-primary);
        }
        sl-details sl-radio::part(checked-icon) {
          color: transparent;
        }
        sl-details sl-radio[aria-checked="true"]::after {
          content: url('data:image/svg+xml; utf8, <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd" stroke-linecap="round"><g stroke="white"><g transform="translate(3.428571, 3.428571)"><path d="M0,5.71428571 L3.42857143,9.14285714"></path><path d="M9.14285714,0 L3.42857143,9.14285714"></path></g></g></g></svg>');
          position: absolute;
          top: calc(var(--dna-space-0-5) / 2);
          left: calc(var(--dna-space-0-5) / 2);
        }
        sl-details sl-radio::part(label) {
          font: var(--dna-body-p2-font);
          line-height: var(--dna-space-4);
        }
      }
    `,
  ]
}

customElements.define('dna-grid-sort', DNAGridSort)
